import React, { useState, useRef, useCallback, useMemo } from 'react'
import { NestedLink } from 'core/plugins/model'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import generateTestId from 'utils/test-helpers'
import { bottomRight } from './defaults'
import Menu, { IMenuProps } from './Menu'
import Text from '../Text'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import clsx from 'clsx'
import Divider from '../Divider'
import NavItem from '../sidebar/NavItem'
import { matchesCurrentPath } from 'core/plugins/route-helpers'
import usePluginRouter from 'core/hooks/usePluginRouter'
import { useSelector } from 'react-redux'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { prop } from 'ramda'
import NavSection from '../sidebar/NavSection'

interface Props extends IMenuProps {
  title: string
  icon?: string
  open: boolean
  anchorEl?: JSX.Element
  nestedLinks: NestedLink[]
  onClose: () => void
  disabled?: boolean
  drawerOpen?: boolean
  k8sClusterAvailable?: boolean
  info?: string
}

export default React.memo(function NavMenu({
  nestedLinks,
  anchorEl = null,
  open,
  onClose,
  icon,
  title,
  className,
  disabled,
  drawerOpen = false,
  k8sClusterAvailable = false,
  info,
  ...props
}: Props) {
  const classes = useStyles({ drawerOpen })
  const { currentPath } = usePluginRouter()
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { virtualNetworking } = session
  const memoizedNestedLinks = useMemo(
    () =>
      nestedLinks?.map((navItem, index) =>
        navItem?.isHidden && navItem?.isHidden({ virtualNetworking }) ? null : (
          <NavSection
            key={index}
            {...navItem}
            isLastChild={nestedLinks[nestedLinks.length - 1] === navItem}
            isActive={!!matchesCurrentPath(currentPath, navItem)}
            k8sClusterAvailable={k8sClusterAvailable}
            disabled={navItem?.isk8sNav && !k8sClusterAvailable}
            info={
              info ? info : navItem?.isk8sNav && !k8sClusterAvailable ? 'No Clusters Available' : ''
            }
            drawerOpen={true}
            nestingLevel={2}
          />
        ),
      ),
    [nestedLinks, currentPath, onClose, disabled, classes],
  )

  return (
    <Menu
      className={clsx(classes.menuContainer, className)}
      data-testid={generateTestId('nav-section', 'menu')}
      origin="left top"
      anchor={anchorEl}
      open={open}
      align={bottomRight.align}
      onClose={onClose}
      {...props}
    >
      <div className={classes.titleContainer}>
        <Text className={clsx(classes.title, 'menu-title-text')} component="div" variant="sidenav1">
          {title}
        </Text>
        {memoizedNestedLinks}
      </div>
    </Menu>
  )
})

interface StyleProps {
  drawerOpen: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  nestedSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  parentItem: {
    fontWeight: 500,
    '&:hover': {
      background: theme.components.sidebar.border,
    },
  },
  subItem: {
    opacity: 0.9,
    fontSize: '0.95em',
  },
  menuContainer: {
    '& .menu-popover': {
      padding: theme.spacing(2, 0, 2, 1),
      background: theme.palette.grey['000'], // theme.components.sidebar.background,
      borderColor: theme.components.sidebar.border,
      borderRadius: theme.spacing(0.5),
    },
  },

  titleContainer: {
    padding: theme.spacing(0, 0, 0, 1.5),
  },
  title: {
    display: 'flex',
    padding: theme.spacing(0, 2, 1, 0),
    height: 24,
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.components.sidebar?.activeText,
  },
  nestedNavItem: {
    padding: theme.spacing(0, 3),
    '&:hover': {
      borderRadius: theme.spacing(0.5),
      background: theme.components.sidebar.border,
      transition: 'background 0.2s ease',
    },

    '& .tooltip-container': {
      marginLeft: theme.spacing(2),
    },
  },
}))
